import { createBrowserRouter, Navigate } from 'react-router-dom';
import AppShell from './components/layout/AppShell.jsx';
import Queue from './routes/queue.jsx';
import Pipeline from './routes/pipeline.jsx';
import TrafficPattern from './routes/traffic.jsx';
import FollowUps from './routes/follow-ups.jsx';
import Renewals from './routes/renewals.jsx';
import QuoteInbox from './routes/quote-inbox.jsx';
import CommandCenter from './routes/command-center.jsx';
import Account from './routes/account.jsx';
import V3Account from './routes/v3-account.jsx';
import Search from './routes/search.jsx';
import More from './routes/more.jsx';
import BlankPHF from './routes/blank-phf.jsx';
import Login from './routes/login.jsx';
import Reset from './routes/reset.jsx';
import PaymentRequest from './routes/payment-request.jsx';
import AdminLayout from './components/admin/AdminLayout.jsx';
import TapAdmin from './routes/tap-admin.jsx';
import TapBatch from './routes/tap-batch.jsx';
import TapProgram from './routes/tap-program.jsx';
import LicensesAdmin from './routes/licenses-admin.jsx';
import CarriersAdmin from './routes/carriers-admin.jsx';
import CarrierDetail from './routes/carrier-detail.jsx';
import SignAdmin from './routes/sign-admin.jsx';
import SignTemplateBuilder from './routes/sign-template-builder.jsx';
import Sign from './routes/sign.jsx';
import AgencyLicenseDetail from './routes/agency-license-detail.jsx';
import ProducerLicenseDetail from './routes/producer-license-detail.jsx';
import ReferralsAdmin from './routes/referrals-admin.jsx';
import TaxesAdmin from './routes/taxes-admin.jsx';
import ContactNamesAdmin from './routes/contact-names-admin.jsx';
import PeopleDoorsAdmin from './routes/people-doors-admin.jsx';
import AutomationsAdmin from './routes/automations-admin.jsx';
import UsersAdmin from './routes/users-admin.jsx';
import ProducerSharesAdmin from './routes/producer-shares-admin.jsx';
import AccountMergeAdmin from './routes/account-merge-admin.jsx';
import ExperienceBacklog from './routes/experience-backlog.jsx'; // TEMPORARY: remove once the backlog is gone
import AircraftModelsAdmin from './routes/aircraft-models-admin.jsx';
import SignInAdmin from './routes/sign-in-admin.jsx';
import DevBnoExplorer from './routes/dev-bno-explorer.jsx';
import MoneyLayout from './components/money/MoneyLayout.jsx';
import MoneySweep from './routes/money-sweep.jsx';
import MoneyAllocate from './routes/money-allocate.jsx';
import MoneyPay from './routes/money-pay.jsx';
import MoneyStatements from './routes/money-statements.jsx';
import MoneyRefunds from './routes/money-refunds.jsx';
import MoneyCommissions from './routes/money-commissions.jsx';
import MoneyTrust from './routes/money-trust.jsx';
import MoneyWeek from './routes/money-week.jsx';
import SignedApplicationReview from './routes/signed-application-review.jsx';
import ImportedDocuments from './routes/imported-documents.jsx'; // paperwork-1 L4-2, screen 9

export const router = createBrowserRouter(
  [
    { path: '/login', element: <Login /> },
    { path: '/reset', element: <Reset /> },
    { path: '/pay/:token', element: <PaymentRequest /> },
    { path: '/v2/pay/:token', element: <PaymentRequest /> },
    { path: '/sign/:token', element: <Sign /> },
    {
      path: '/',
      element: <AppShell />,
      children: [
        // MV-10 3.1 (Ship 4a): the what's-waiting queue IS the landing
        // surface. The old SmartDefaultRedirect (last-route restore) is gone;
        // every prior landing target keeps its route one click away.
        { index: true, element: <Queue /> },
        { path: 'pipeline', element: <Pipeline /> },
        { path: 'follow-ups', element: <FollowUps /> },
        { path: 'renewals', element: <Renewals /> },
        { path: 'quote-inbox', element: <QuoteInbox /> },
        { path: 'traffic', element: <TrafficPattern /> },
        { path: 'traffic/conversation/:contactId', element: <TrafficPattern /> },
        { path: 'pattern', element: <Navigate to="/traffic" replace /> },
        { path: 'pattern/conversation/:contactId', element: <Navigate to="/traffic" replace /> },
        { path: 'command-center', element: <CommandCenter /> },
        { path: 'command-center/:section', element: <CommandCenter /> },
        { path: 'triage', element: <Navigate to="/traffic" replace /> },
        { path: 'account/:idOrSlug', element: <Account /> },
        { path: 'account/:idOrSlug/deal/:dealId', element: <Account /> },
        // V3 overnight 1, Track C: the v3 account page. Not in any nav.
        { path: 'v3/account/:id', element: <V3Account /> },
        { path: 'search', element: <Search /> },
        { path: 'more', element: <More /> },
        { path: 'blank-phf', element: <BlankPHF /> },
        { path: 'signed-applications/review', element: <SignedApplicationReview /> },
        { path: 'documents/imports', element: <ImportedDocuments /> },
        // clearedto.rent dev tooling: the BNO rating explorer (UAT only,
        // broker JWT; the API refuses non-UAT hosts server-side).
        { path: 'dev/bno-explorer', element: <DevBnoExplorer /> },
        // Money section (TRACON-MONEY-SPEC §6 §9): the trust number is the
        // landing (§9.2); sweep, allocate, pay, statements beside it.
        {
          path: 'money',
          element: <MoneyLayout />,
          children: [
            // R36 (NIGHT-13): Money this week is the money home; the trust cards sit beside it.
            { index: true, element: <MoneyWeek /> },
            { path: 'trust', element: <MoneyTrust /> },
            { path: 'sweep', element: <MoneySweep /> },
            { path: 'allocate', element: <MoneyAllocate /> },
            { path: 'pay', element: <MoneyPay /> },
            { path: 'statements', element: <MoneyStatements /> },
            { path: 'refunds', element: <MoneyRefunds /> },
            { path: 'commissions', element: <MoneyCommissions /> },
          ],
        },
        // Admin section — TAP is the first surface. Carriers/Rules/Brokers
        // land as sibling sub-routes later (see AdminLayout sub-nav).
        {
          path: 'admin',
          element: <AdminLayout />,
          children: [
            { index: true, element: <Navigate to="tap" replace /> },
            { path: 'tap', element: <TapAdmin /> },
            { path: 'tap/batch/:label', element: <TapBatch /> },
            { path: 'tap/program/:id', element: <TapProgram /> },
            { path: 'carriers', element: <CarriersAdmin /> },
            { path: 'carriers/:slug', element: <CarrierDetail /> },
            { path: 'licenses', element: <LicensesAdmin /> },
            { path: 'licenses/agency/:id', element: <AgencyLicenseDetail /> },
            { path: 'licenses/producer/:id', element: <ProducerLicenseDetail /> },
            { path: 'sign', element: <SignAdmin /> },
            { path: 'sign/:id', element: <SignTemplateBuilder /> },
            { path: 'referrals', element: <ReferralsAdmin /> },
            { path: 'taxes', element: <TaxesAdmin /> },
            { path: 'contact-names', element: <ContactNamesAdmin /> },
            { path: 'people-doors', element: <PeopleDoorsAdmin /> },
            { path: 'automations', element: <AutomationsAdmin /> },
            { path: 'users', element: <UsersAdmin /> },
            { path: 'producer-shares', element: <ProducerSharesAdmin /> },
            { path: 'account-merge', element: <AccountMergeAdmin /> },
            { path: 'experience-backlog', element: <ExperienceBacklog /> },
            { path: 'aircraft-models', element: <AircraftModelsAdmin /> },
            { path: 'sign-in', element: <SignInAdmin /> },
          ],
        },
        // Legacy /v2/* redirects
        { path: 'v2/*', element: <Navigate to="/" replace /> },
      ],
    },
    // Catch legacy /v2/login
    { path: '/v2/login', element: <Navigate to="/login" replace /> },
    { path: '/v2/*', element: <Navigate to="/" replace /> },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);
